import { ChannelPreview } from "components/ChannelPreview/ChannelPreview";
import { ChatSearch } from "components/ChatSearch/ChatSearch";
import { ChatFilterError } from "./ChatFilterError";
import Loader from "components/DesignSystem/Loader/loader";
import { EmptyChannelList } from "components/EmptyChannelList";
import { StreamChannelList } from "components/StreamChannelList/StreamChannelList";
import { useChatFilterContext } from "contexts/ChatFilterContext";
import { useAppSelector } from "hooks/useAppSelector";
import { useChatFilterPicks } from "hooks/useChatFilterPicks";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { matchesChatFilters } from "util/matchesChatFilters";

import { useContext, useEffect, useMemo } from "react";
import { Channel, ChannelFilters } from "stream-chat";
import {
  ChannelListMessenger,
  ChannelListMessengerProps,
  InfiniteScroll,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { filterExcludedChannels } from "util/isChannelLastMessageExcluded";
import { SelectedChannelsContext } from "./SelectedChannelContext";
import { UnrepliedTabView } from "./UnrepliedTabView";
import { UnrepliedView } from "./UnrepliedView";

const ChannelPreviewWrapper = (props: any) => {
  return (
    <ChannelPreview
      {...props}
      unreplied={props.unreplied}
      {...(props.onSelectCallback
        ? {
            onSelect: () => props.onSelectCallback(props.channel.id),
          }
        : {})}
    />
  );
};

const ChannelListWithSync = (props: ChannelListMessengerProps) => {
  const { setChannelsForSource } = useContext(SelectedChannelsContext);
  const loadedInfos = (props.loadedChannels || [])
    .filter((c) => Boolean(c?.id))
    .map((c) => ({
      id: c.id as string,
      needsReply: Boolean(c.data?.needs_reply),
    }));
  const loadedInfosKey = loadedInfos
    .map((c) => `${c.id}:${c.needsReply ? 1 : 0}`)
    .join(",");

  useEffect(() => {
    setChannelsForSource("default", loadedInfos);
  }, [loadedInfosKey]);

  useEffect(
    () => () => setChannelsForSource("default", []),
    [setChannelsForSource]
  );

  return <ChannelListMessenger {...props} />;
};

export const CustomisedChannelLists = ({
  isResolvingChannels,
  hasFilterError,
  searchValue,
  onSelectCallback,
  filters,
  sort,
  unreplied,
  customActiveChannel,
  onChannelUpdated,
  hideChannelCheckboxSelect,
}: {
  isResolvingChannels?: boolean;
  hasFilterError?: boolean;
  searchValue: string | null;
  onSelectCallback: (channelId?: string, messageId?: string) => void;
  filters: ChannelFilters<DefaultStreamChatGenerics>;
  sort: any;
  unreplied: boolean;
  customActiveChannel: string | undefined;
  onChannelUpdated: (
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
    >
  ) => void;
  hideChannelCheckboxSelect?: boolean;
}) => {
  const { isCustomer } = useRoleBasedView();
  const picks = useChatFilterPicks();
  const {
    filters: { EXCLUDE: excludeOptions },
  } = useChatFilterContext();
  const { showMentionedMessages, sortByNewest, showUnrepliedMessages } =
    useAppSelector((state) => state.chatSearch);

  const { isUnreadOnly, isPendingActionOnly } = picks;

  const channelRenderFilterFn = useMemo(
    () => (channels: Array<Channel<DefaultStreamChatGenerics>>) =>
      filterExcludedChannels(channels, excludeOptions).filter((channel) =>
        matchesChatFilters(channel, { isUnreadOnly, isPendingActionOnly })
      ),
    [excludeOptions, isUnreadOnly, isPendingActionOnly]
  );

  if (isResolvingChannels) {
    return <Loader />;
  }

  if (hasFilterError) {
    return <ChatFilterError />;
  }

  if (searchValue || showMentionedMessages) {
    return (
      <ChatSearch
        onSelect={onSelectCallback}
        filters={{
          ...filters,
          needs_reply: filters.needs_reply || showUnrepliedMessages,
        }}
        sort={sort}
        unreplied={unreplied}
      />
    );
  }

  if (isCustomer) {
    return (
      <StreamChannelList<DefaultStreamChatGenerics>
        EmptyStateIndicator={EmptyChannelList}
        Paginator={(props) => <InfiniteScroll {...props} threshold={400} />}
        Preview={(props) => (
          <ChannelPreviewWrapper
            {...props}
            unreplied={unreplied}
            onSelectCallback={onSelectCallback}
          />
        )}
        filters={filters}
        customActiveChannel={customActiveChannel}
        setActiveChannelOnMount={false}
        sort={[sort]}
        options={{ limit: 10 }}
        onChannelUpdated={onChannelUpdated}
        channelRenderFilterFn={channelRenderFilterFn}
      />
    );
  }

  if (unreplied) {
    return (
      <UnrepliedTabView
        onSelectCallback={onSelectCallback}
        filters={filters}
        sort={sort}
        unreplied={unreplied}
        onChannelUpdated={onChannelUpdated}
      />
    );
  }

  if (showUnrepliedMessages) {
    filters = {
      ...filters,
      needs_reply: true,
    };

    return (
      <UnrepliedView
        filters={filters}
        sort={sort}
        onSelectCallback={onSelectCallback}
        unreplied
        withWrapper={false}
        onChannelUpdated={onChannelUpdated}
      />
    );
  }

  // Default view
  return (
    <StreamChannelList<DefaultStreamChatGenerics>
      EmptyStateIndicator={EmptyChannelList}
      Paginator={(props) => <InfiniteScroll {...props} threshold={400} />}
      Preview={(props) => (
        <ChannelPreviewWrapper
          {...props}
          unreplied={unreplied}
          onSelectCallback={onSelectCallback}
          hideChannelCheckboxSelect={hideChannelCheckboxSelect}
        />
      )}
      filters={filters}
      customActiveChannel={customActiveChannel}
      setActiveChannelOnMount={false}
      sort={[sort]}
      options={{ limit: 10 }}
      onChannelUpdated={onChannelUpdated}
      channelRenderFilterFn={channelRenderFilterFn}
      List={ChannelListWithSync}
      sendChannelsToList
    />
  );
};
